export type FiqueRegistro = {
  doc: string
  nome: string
  mats: string
  saldo: string
  nf: number
  venc: string
  dias: number
  cad_unico: boolean
  desc_av_ff: string
  pg_av_ff: string
  desc_pa_ff: string
  pg_pa_ff: string
  desc_av_am: string
  pg_av_am: string
  desc_pa_am: string
  pg_pa_am: string
  mp: number
}

export type FiqueRejectedRecord = {
  line: number
  reason: string
  values: string[]
}

export type FiqueParseResult = {
  registros: FiqueRegistro[]
  rejected: FiqueRejectedRecord[]
  headers: string[]
  fatal: string[]
  sourceRows: number
  invalid: string[]
  duplicates: number
  referenceDate: string | null
  documentStats: { cpfValidos: number; cnpjValidos: number; cpfInvalidos: number; cnpjInvalidos: number; irrecuperaveis: number }
}

const REQUIRED = [
  'doc', 'nome', 'mats', 'saldo', 'nf', 'venc', 'dias', 'cad_unico',
  'desc_av_ff', 'pg_av_ff', 'desc_pa_ff', 'pg_pa_ff',
  'desc_av_am', 'pg_av_am', 'desc_pa_am', 'pg_pa_am', 'mp',
] as const
const MONEY = [
  'saldo', 'desc_av_ff', 'pg_av_ff', 'desc_pa_ff',
  'pg_pa_ff', 'desc_av_am', 'pg_av_am', 'desc_pa_am', 'pg_pa_am',
] as const

export function cpfValido(value: string): boolean {
  const d = value.replace(/\D/g, '')
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false
  for (let position = 9; position <= 10; position++) {
    let sum = 0
    for (let index = 0; index < position; index++) {
      sum += Number(d[index]) * (position + 1 - index)
    }
    if (Number(d[position]) !== (sum * 10 % 11) % 10) return false
  }
  return true
}

export function cnpjValido(value: string): boolean {
  const d = value.replace(/\D/g, '')
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false
  const weights = [
    [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
    [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
  ]
  for (let index = 0; index < 2; index++) {
    const sum = weights[index].reduce((total, weight, pos) => total + Number(d[pos]) * weight, 0)
    const rest = sum % 11
    if (Number(d[12 + index]) !== (rest < 2 ? 0 : 11 - rest)) return false
  }
  return true
}

export function documentoValido(value: string): boolean {
  const d = value.replace(/\D/g, '')
  return d.length === 11 ? cpfValido(d) : d.length === 14 && cnpjValido(d)
}

export type FiqueDocumentKind = 'cpf' | 'cnpj' | 'irrecuperavel'
export function classificarDocumentoImportacao(value: unknown): { doc: string; kind: FiqueDocumentKind; valido: boolean } {
  const raw = String(value ?? '').trim()
  // Exportações em notação científica podem ter perdido dígitos: jamais reconstruir por arredondamento.
  if (!raw || /[eE]/.test(raw)) return { doc: '', kind: 'irrecuperavel', valido: false }
  let digits = ''
  if (/^\d{1,14}(?:\.0+)?$/.test(raw)) {
    digits = raw.replace(/\.0+$/, '')
  } else if (/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(raw) ||
             /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(raw)) {
    digits = raw.replace(/\D/g, '')
  } else {
    return { doc: '', kind: 'irrecuperavel', valido: false }
  }
  // Numerais exportados pelo Excel podem perder zeros iniciais.
  // 1-11 dígitos = CPF; 12-14 dígitos = CNPJ.
  const kind = digits.length > 11 ? 'cnpj' : 'cpf'
  const doc = digits.padStart(kind === 'cpf' ? 11 : 14, '0')
  return { doc, kind, valido: kind === 'cpf' ? cpfValido(doc) : cnpjValido(doc) }
}

export function normalizarDocumentoImportacao(value: unknown): string {
  const result = classificarDocumentoImportacao(value)
  return result.valido ? result.doc : ''
}

export function normalizarCpfImportacao(value: unknown): string {
  const result = classificarDocumentoImportacao(value)
  return result.valido && result.kind === 'cpf' ? result.doc : ''
}

export function mascaraDocumento(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14)
  if (d.length === 14) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12)
  if (d.length === 11) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9)
  return d
}

export const mascaraCpf = mascaraDocumento

function isoDate(value: string): string {
  const trimmed = value.trim()
  const br = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed)
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  const year = Number(br?.[3] ?? iso?.[1])
  const month = Number(br?.[2] ?? iso?.[2])
  const day = Number(br?.[1] ?? iso?.[3])
  if (!year || !month || !day) return ''
  const normalized = new Date(Date.UTC(year, month - 1, day))
  if (normalized.getUTCFullYear() !== year || normalized.getUTCMonth() + 1 !== month || normalized.getUTCDate() !== day) return ''
  return String(year).padStart(4, '0') + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0')
}

function cents(value: string): number | null {
  const raw = value.trim()
  let canonical = ''
  if (/^\d+(\.\d{1,2})?$/.test(raw)) canonical = raw
  else if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(raw)) canonical = raw.replace(/\./g, '').replace(',', '.')
  else if (/^\d+,\d{1,2}$/.test(raw)) canonical = raw.replace(',', '.')
  else return null
  const parts = canonical.split('.')
  const result = Number(parts[0]) * 100 + Number((parts[1] ?? '').padEnd(2, '0'))
  return Number.isSafeInteger(result) && result >= 0 ? result : null
}

function money(value: number) {
  return (value / 100).toFixed(2)
}

export function parseFiqueEmDiaRows(rows: unknown[][]): FiqueParseResult {
  const originalHeaders = (rows[0] ?? []).map((value) => String(value ?? '').trim().replace(/^\uFEFF/, ''))
  const header = originalHeaders.map((value) => value.toLowerCase())
  const documentStats = { cpfValidos: 0, cnpjValidos: 0, cpfInvalidos: 0, cnpjInvalidos: 0, irrecuperaveis: 0 }
  const fatal: string[] = []
  const invalid: string[] = []
  const rejected: FiqueRejectedRecord[] = []
  const missing = REQUIRED.filter((name) => !header.includes(name))
  if (missing.length) {
    const message = 'Colunas obrigatórias ausentes: ' + missing.join(', ')
    return {
      registros: [], rejected, headers: originalHeaders, fatal: [message],
      sourceRows: 0, invalid: [message], duplicates: 0, referenceDate: null, documentStats,
    }
  }
  if (new Set(header).size !== header.length) {
    const message = 'Há cabeçalhos duplicados; corrija antes de importar.'
    return {
      registros: [], rejected, headers: originalHeaders, fatal: [message],
      sourceRows: 0, invalid: [message], duplicates: 0, referenceDate: null, documentStats,
    }
  }
  const indexes = Object.fromEntries(header.map((name, index) => [name, index]))
  // Guardar os dados originais somente na memória do ADMIN, para gerar o relatório de exclusões.
  const candidates: { record: FiqueRegistro; line: number; values: string[] }[] = []
  const namesByDocument = new Map<string, string>()
  const matriculasByDocument = new Map<string, Set<string>>()
  const blockedDocuments = new Map<string, string>()
  let duplicates = 0
  let sourceRows = 0
  let referenceDate: string | null = null

  const reject = (line: number, values: string[], reason: string) => {
    rejected.push({ line, values, reason })
    invalid.push('Linha ' + line + ': ' + reason)
  }

  for (let index = 1; index < rows.length; index++) {
    const row = rows[index] ?? []
    if (row.every((value) => !String(value ?? '').trim())) continue
    const line = index + 1
    sourceRows++
    const values = originalHeaders.map((_, col) => String(row[col] ?? ''))
    const get = (name: string) => String(row[indexes[name]] ?? '').trim()
    const document = classificarDocumentoImportacao(get('doc'))
    const doc = document.valido ? document.doc : ''
    const nome = get('nome')
    const mats = get('mats')
    const nf = Number(get('nf'))
    const dias = Number(get('dias'))
    const mp = Number(get('mp'))
    const venc = isoDate(get('venc'))
    const cad = get('cad_unico').toUpperCase()
    const amounts = Object.fromEntries(MONEY.map((key) => [key, cents(get(key))])) as Record<typeof MONEY[number], number | null>

    if (!doc) {
      const reason = document.kind === 'cpf' ? 'CPF inválido (dígitos verificadores).' :
        document.kind === 'cnpj' ? 'CNPJ inválido (dígitos verificadores).' :
          'Documento ausente, malformado ou com precisão perdida.'
      if (document.kind === 'cpf') documentStats.cpfInvalidos++
      else if (document.kind === 'cnpj') documentStats.cnpjInvalidos++
      else documentStats.irrecuperaveis++
      reject(line, values, reason)
      continue
    }
    if (document.kind === 'cpf') documentStats.cpfValidos++
    else documentStats.cnpjValidos++

    // Documento compartilhado por pessoas distintas é ambíguo.
    // Excluir TODOS os registros desse documento, inclusive os anteriores.
    const canonicalName = nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLocaleUpperCase('pt-BR').replace(/\s+/g, ' ').trim()
    if (canonicalName) {
      const existing = namesByDocument.get(doc)
      if (existing && existing !== canonicalName) {
        blockedDocuments.set(doc, 'Documento associado a nomes divergentes: todos os vínculos excluídos por segurança.')
        reject(line, values, 'Documento associado a nomes divergentes: todos os vínculos excluídos por segurança.')
        continue
      }
      if (!existing) namesByDocument.set(doc, canonicalName)
    }
    if (blockedDocuments.has(doc)) {
      reject(line, values, blockedDocuments.get(doc)!)
      continue
    }

    if (!nome || nome.length > 180 || !mats || mats.length > 200 || !/^[0-9,\s]+$/.test(mats)) {
      reject(line, values, 'Identificação ou matrícula inválida.')
      continue
    }
    const matsList = mats.split(',').map((value) => value.trim())
    if (matsList.some((value) => !/^\d+$/.test(value)) || new Set(matsList).size !== matsList.length) {
      reject(line, values, 'Lista de matrículas inconsistente.')
      continue
    }
    const usedMatriculas = matriculasByDocument.get(doc) ?? new Set<string>()
    if (matsList.some((value) => usedMatriculas.has(value))) {
      duplicates++
      blockedDocuments.set(doc, 'Matrícula repetida para o mesmo documento: todos os vínculos excluídos por segurança.')
      reject(line, values, blockedDocuments.get(doc)!)
      continue
    }
    if (!Number.isSafeInteger(nf) || nf < 0 || !Number.isSafeInteger(dias) || dias < 0 ||
        !Number.isSafeInteger(mp) || mp < 1 || mp > 120 || !venc || (cad !== 'SIM' && cad !== 'NAO')) {
      reject(line, values, 'Faturas, atraso, vencimento, parcelas ou Cadastro Único inválidos.')
      continue
    }
    if (MONEY.some((key) => amounts[key] === null)) {
      reject(line, values, 'Valor financeiro inválido.')
      continue
    }
    const saldo = amounts.saldo!
    if (saldo <= 0 ||
        amounts.desc_av_ff! + amounts.pg_av_ff! !== saldo ||
        amounts.desc_pa_ff! + amounts.pg_pa_ff! !== saldo ||
        amounts.desc_av_am! + amounts.pg_av_am! !== saldo ||
        amounts.desc_pa_am! + amounts.pg_pa_am! !== saldo) {
      reject(line, values, 'Saldo não confere com desconto e valor final.')
      continue
    }
    const inferredDate = new Date(Date.parse(venc + 'T00:00:00Z') + dias * 86400000)
    const inferredReference = Number.isFinite(inferredDate.valueOf()) && dias <= 200000
      ? inferredDate.toISOString().slice(0, 10) : ''
    if (!inferredReference || (referenceDate && inferredReference !== referenceDate)) {
      reject(line, values, 'Data-base divergente das demais linhas.')
      continue
    }
    referenceDate = inferredReference
    matsList.forEach((mat) => usedMatriculas.add(mat))
    matriculasByDocument.set(doc, usedMatriculas)
    candidates.push({
      line, values,
      record: {
        doc, nome, mats, saldo: money(saldo), nf, venc, dias,
        cad_unico: cad === 'SIM',
        desc_av_ff: money(amounts.desc_av_ff!), pg_av_ff: money(amounts.pg_av_ff!),
        desc_pa_ff: money(amounts.desc_pa_ff!), pg_pa_ff: money(amounts.pg_pa_ff!),
        desc_av_am: money(amounts.desc_av_am!), pg_av_am: money(amounts.pg_av_am!),
        desc_pa_am: money(amounts.desc_pa_am!), pg_pa_am: money(amounts.pg_pa_am!),
        mp,
      },
    })
  }

  const registros: FiqueRegistro[] = []
  for (const candidate of candidates) {
    const conflict = blockedDocuments.get(candidate.record.doc)
    if (conflict) reject(candidate.line, candidate.values, conflict)
    else registros.push(candidate.record)
  }

  rejected.sort((a, b) => a.line - b.line)
  invalid.length = 0
  for (const item of rejected) invalid.push('Linha ' + item.line + ': ' + item.reason)
  if (!sourceRows) fatal.push('Arquivo sem registros.')
  if (!registros.length) fatal.push('Nenhum registro íntegro disponível para publicação.')
  if (!referenceDate) fatal.push('Não foi possível identificar a data-base a partir de vencimento e dias de atraso.')
  if (registros.length + rejected.length !== sourceRows) {
    fatal.push('Inconsistência no total de linhas; publicação cancelada.')
  }
  invalid.push(...fatal)
  return {
    registros, rejected, headers: originalHeaders, fatal, sourceRows,
    invalid, duplicates, referenceDate, documentStats,
  }
}

// CSV RFC 4180 com quebras de linha e aspas escapadas; sem registrar dados pessoais.
export function parseFiqueCsv(text: string): unknown[][] {
  const input = text.replace(/^\uFEFF/, '')
  const firstLine = input.split(/\r?\n/, 1)[0] ?? ''
  const sep = [';', '\t', ','].sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0]
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < input.length; i++) {
    const char = input[i]
    if (char === '"') {
      if (quoted && input[i + 1] === '"') { cell += '"'; i++ }
      else quoted = !quoted
    } else if (char === sep && !quoted) {
      row.push(cell); cell = ''
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && input[i + 1] === '\n') i++
      row.push(cell); rows.push(row); row = []; cell = ''
    } else {
      cell += char
    }
  }
  if (quoted) throw new Error('Arquivo CSV possui aspas sem fechamento.')
  row.push(cell)
  if (row.some((value) => value.length)) rows.push(row)
  return rows
}

export async function checksumFiqueEmDia(registros: FiqueRegistro[], data: string) {
  const raw = data + '\n' + registros.map((r) => JSON.stringify(r)).sort().join('\n')
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('')
}
